import { Button, Heading, Hr, Icon } from '@pandiafi/design-system'
import { useFormikContext } from 'formik'
import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { FormValues } from '../../../TerminateForm/initialValues'
import { BeneficiaryNameField } from './BeneficiaryNameField/BeneficiaryNameField'
import { BeneficiaryIbanField } from './BeneficiaryIbanField/BeneficiaryIbanField'
import { BeneficiaryReturnPercentageField } from './BeneficiaryReturnPercentageField/BeneficiaryReturnPercentageField'
import { BeneficiaryEmailField } from './BeneficiaryEmailField/BeneficiaryEmailField'
import { BeneficiaryPhoneNumberField } from './BeneficiaryPhoneNumberField/BeneficiaryPhoneNumberField'
import { BeneficiaryBicField } from './BeneficiaryBicField/BeneficiaryBicField'
import { BeneficiaryBankCountryField } from './BeneficiaryBankCountryField/BeneficiaryBankCountryField'
import { useBicField } from './useBicField'
import { ContractType } from '../../../../../../common/types'

import { notNullOrUndefined } from 'utils/notNullOrUndefined'

interface BeneficiaryProps {
  index: number
}

export const Beneficiary: React.FC<BeneficiaryProps> = ({ index }) => {
  const { t } = useTranslation(['terminateContract'])
  const { values: formValues, setFieldValue } = useFormikContext<FormValues>()

  const row = formValues.beneficiaries[index]
  const { beneficiaryName, iban, bic, bankCountryCode } = row
  const showRooReturnPercentage =
    formValues.contractType === ContractType.RIGHT_OF_OCCUPANCY

  const { isBicFieldVisible } = useBicField(
    bankCountryCode,
    iban,
    bic,
    `beneficiaries[${index}]bic`,
    setFieldValue
  )

  const required = useMemo(() => {
    const hasName = !!beneficiaryName?.trim()
    const hasIban = !!iban?.trim()

    // If either name or IBAN is filled, all fields become required
    return hasName || hasIban
  }, [beneficiaryName, iban])

  const handleRemoveBeneficiary = () => {
    const hasRooReturnAllocation = formValues.beneficiaries.some(
      (beneficiary) =>
        beneficiary.removeExistingBeneficiary !== true &&
        beneficiary.rooReturnPercentage !== null
    )
    // Fetch the account details that still exist after deletion
    const beneficiaries = formValues.beneficiaries
      .map((beneficiary, indx) => {
        if (indx === index) {
          if (beneficiary.existingBeneficiaryId) {
            return {
              ...beneficiary,
              removeExistingBeneficiary: true,
              returnPercentage: 0,
              rooReturnPercentage:
                beneficiary.rooReturnPercentage === null ? null : 0,
            }
          }
          return null
        }
        return beneficiary
      })
      .filter(notNullOrUndefined)

    // find the remaining account details, and if there is only one,
    // update its refund percentage to 100%.
    const notRemovedBeneficiaries = beneficiaries.filter(
      (b) => b.removeExistingBeneficiary !== true
    )
    const onlyExistingBeneficiaryIndex =
      notRemovedBeneficiaries.length === 1
        ? // eslint-disable-next-line no-object-comparison/array-includes-object
          beneficiaries.indexOf(notRemovedBeneficiaries[0])
        : undefined

    const updatedBeneficiaries = beneficiaries.map((b, indx) => {
      if (onlyExistingBeneficiaryIndex === indx) {
        return {
          ...b,
          returnPercentage: 100,
          rooReturnPercentage: hasRooReturnAllocation
            ? 100
            : b.rooReturnPercentage,
        }
      }
      return b
    })

    setFieldValue('beneficiaries', updatedBeneficiaries)
  }

  if (row?.removeExistingBeneficiary) {
    return null
  }

  const displayName =
    beneficiaryName && beneficiaryName.trim().length > 0
      ? beneficiaryName
      : t('terminateContract:texts.addPaymentInfo')

  return (
    <>
      <div className="flex justify-between items-center">
        <Heading size="m" element="h4" className="mb-4 phone:font-medium">
          {displayName}
        </Heading>

        {formValues.beneficiaries.length > 0 && (
          <Button
            type="button"
            variant="icon"
            className="p-2 -m-2"
            onClick={() => handleRemoveBeneficiary()}
          >
            <Icon icon="close-window" />
          </Button>
        )}
      </div>

      <BeneficiaryNameField index={index} required={required} />
      <BeneficiaryIbanField index={index} required={required} />
      <BeneficiaryBankCountryField index={index} required={required} />
      {isBicFieldVisible && (
        <BeneficiaryBicField index={index} required={required} />
      )}

      <BeneficiaryReturnPercentageField
        index={index}
        fieldName="returnPercentage"
        label={t('terminateContract:labels.returnPercentage')}
        valueType="number"
        required={required}
      />
      {showRooReturnPercentage && (
        <BeneficiaryReturnPercentageField
          index={index}
          fieldName="rooReturnPercentage"
          label={t('terminateContract:labels.rooReturnPercentage')}
          valueType="number"
        />
      )}

      <div className="min-tablet:flex min-tablet:gap-2 min-tablet:mb-8">
        <BeneficiaryEmailField index={index} />
        <BeneficiaryPhoneNumberField index={index} />
      </div>

      <Hr className="my-10 border-[#cbcbcb]" />
    </>
  )
}
